/**
 * Custom Metric Widget (Story 5.28, Task 4 → Epic 37 redesign — clean stat card)
 *
 * Displays a user-defined calculated metric from multiple data sources.
 * Edit mode: static placeholder with formula preview, variable count, output format badge.
 * Preview/Generate mode: KPI-style card with calculated value and optional comparison.
 *
 * Shares the chart-widget shell (quiet uppercase title, size-aware padding,
 * compact preview frame) so the canvas widget and the sidebar preview render
 * identically with the rest of the redesigned widget family. Value-first
 * hierarchy: the big neutral value leads; the delta is a colored verdict pill
 * below it. Reuses KPI formatting/trend from @provenleap/types/report-rendering.
 */

import type { ReactNode } from "react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { KpiData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isKpiData } from "../../../lib/widgetData";
import { TREND_COLORS } from "../../../lib/chartConstants";
import {
  getKpiFontSize,
  calculateTrend,
} from "@provenleap/types/report-rendering";
import { GearSix, Warning } from "@phosphor-icons/react";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

interface CustomMetricWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<KpiData>;
}

interface CustomMetricConfig {
  formulaExpression?: string;
  variables?: Array<{ id: string; label: string; metricKey: string }>;
  outputFormat?: "number" | "currency" | "percentage";
  decimalPlaces?: number;
  comparisonEnabled?: boolean;
  title?: string;
  currencyCode?: string;
}

function formatCustomValue(
  value: number,
  outputFormat: string,
  decimalPlaces: number,
  currencyCode: string
): string {
  const isLarge = Math.abs(value) >= 1_000_000;

  if (outputFormat === "percentage") {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value / 100);
  }

  if (outputFormat === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      notation: isLarge ? "compact" : "standard",
      minimumFractionDigits: isLarge ? 1 : decimalPlaces,
      maximumFractionDigits: isLarge ? 1 : decimalPlaces,
    }).format(value);
  }

  return new Intl.NumberFormat("en-US", {
    notation: isLarge ? "compact" : "standard",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
}

/** Delta verdict pill — color on the delta (not the value) + arrow (a11y). */
function TrendIndicator({ direction, percentage }: { direction: "up" | "down" | "neutral"; percentage: number }) {
  const color = TREND_COLORS[direction];
  const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";

  return (
    <span
      role="status"
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[12px] font-semibold leading-none whitespace-nowrap"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
      aria-label={`Trend ${direction} ${percentage.toFixed(1)}%`}
    >
      <span aria-hidden>{arrow}</span>
      <span>{percentage.toFixed(1)}%</span>
    </span>
  );
}

function EditModePlaceholder({ config, compact }: { config: CustomMetricConfig; compact: boolean }) {
  const formula = config.formulaExpression || "";
  const variables = config.variables || [];
  const outputFormat = config.outputFormat || "number";
  const currencyCode = config.currencyCode || "USD";
  const hasFormula = formula.trim().length > 0;
  const hasVariables = variables.length > 0;
  const isValid = hasFormula && hasVariables;

  const formulaPreview = formula.length > 60 ? formula.slice(0, 57) + "..." : formula;

  const formatLabel = outputFormat === "currency"
    ? `Currency (${currencyCode})`
    : outputFormat === "percentage"
      ? "Percentage"
      : "Number";

  return (
    <div
      data-testid="custom-metric-placeholder"
      className="flex h-full w-full flex-col items-center justify-center gap-2 text-center"
    >
      <GearSix size={28} className="text-muted-foreground/50" />
      {!compact && (
        <span className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
          Custom Metric
        </span>
      )}

      {hasFormula ? (
        <code
          className="max-w-full truncate rounded bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
          data-testid="custom-metric-formula-preview"
          title={formula}
        >
          {formulaPreview}
        </code>
      ) : (
        <span className="text-[10px] italic text-muted-foreground">No formula configured</span>
      )}

      {!compact && (
        <div className="mt-1 flex items-center gap-2">
          {hasVariables && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {variables.length} variable{variables.length !== 1 ? "s" : ""}
            </span>
          )}
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {formatLabel}
          </span>
        </div>
      )}

      {!isValid && (
        <div className="mt-1 flex items-center gap-1 text-amber-500" data-testid="custom-metric-warning">
          <Warning size={14} />
          <span className="text-[10px]">Configure formula in settings panel</span>
        </div>
      )}
    </div>
  );
}

export function CustomMetricWidget({ widget, data }: CustomMetricWidgetProps) {
  const config = widget.config as CustomMetricConfig;
  const colSpan = widget.position.colSpan;
  const { compact, shell } = useChartWidgetShell(widget.config);
  const title = config.title || widget.title;

  const shellBody = (body: ReactNode) => shell("custom-metric", title, body, { centered: true });

  // Edit mode: no data prop means we're in canvas edit mode
  if (!data) {
    return shellBody(<EditModePlaceholder config={config} compact={compact} />);
  }

  // Loading state
  if (data.loading) {
    return shellBody(<div className="h-8 w-24 animate-pulse rounded bg-muted" />);
  }

  // Error state
  if (data.error) {
    return shellBody(
      <ChartMessage role="alert">Unable to calculate — check formula variables</ChartMessage>
    );
  }

  // No data state
  const kpiData = data.data;
  if (!kpiData || !isKpiData(kpiData)) {
    return shellBody(<span className="text-sm text-muted-foreground">{"—"}</span>);
  }

  // Preview/Generate mode: KPI-style card
  const outputFormat = config.outputFormat || "number";
  const decimalPlaces = config.decimalPlaces ?? 2;
  const currencyCode = config.currencyCode || "USD";

  const formattedValue = formatCustomValue(kpiData.value, outputFormat, decimalPlaces, currencyCode);
  const fontSize = getKpiFontSize(formattedValue, colSpan);
  const trend = kpiData.previousValue != null
    ? calculateTrend(kpiData.value, kpiData.previousValue)
    : null;

  return shellBody(
    <div className="flex w-full flex-col">
      <div
        className="truncate font-semibold text-foreground"
        style={{ fontSize: `${fontSize}px`, lineHeight: 1.1, letterSpacing: "-0.01em" }}
        data-testid="custom-metric-value"
        title={formattedValue}
      >
        {formattedValue}
      </div>
      {trend && (
        <div className="mt-2 flex items-center gap-2" data-testid="custom-metric-comparison">
          <TrendIndicator direction={trend.direction} percentage={trend.percentage} />
          {!compact && (
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">vs. previous period</span>
          )}
        </div>
      )}
    </div>,
  );
}
